// frontend/packages/business-console/src/components/content/EditorPane.jsx

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PublishIcon from '@mui/icons-material/Publish';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import LaunchIcon from '@mui/icons-material/Launch';

import FrontmatterField from './FrontmatterField';
import SeoAssistCard from './SeoAssistCard';
import CopilotBar from './CopilotBar';
import SettingsTabs from './SettingsTabs';
import BlogContextCard from './BlogContextCard';
import SaveStatusIndicator from './SaveStatusIndicator';
import PublishConfirmDialog from './PublishConfirmDialog';
import {
  buildContentPath,
  stripMarkdownExtension,
  humanizeLabel,
  validateRequired,
  setAtPath,
} from '../../utils/contentEditorHelpers';
import MediaPickerModal from '../media/MediaPickerModal';
import InsertModeDialog from '../media/InsertModeDialog';
import { toCldToken } from '../../utils/mediaToken';
import { markRecentlyUsed } from '../../services/mediaService';
import { Surface, StatusPill } from '../primitives';

const safe = (v) => (v == null ? '' : String(v));

function insertAtCursor(textarea, insertText) {
  if (!textarea) return { next: insertText, caret: insertText.length };
  const value = textarea.value || '';
  const start =
    typeof textarea.selectionStart === 'number'
      ? textarea.selectionStart
      : value.length;
  const end =
    typeof textarea.selectionEnd === 'number'
      ? textarea.selectionEnd
      : value.length;
  const before = value.slice(0, start);
  const after = value.slice(end);
  const next = `${before}${insertText}${after}`;
  const caret = start + insertText.length;
  return { next, caret };
}

function buildInsertSnippet(asset, insertMode) {
  const type = String(asset?.type || asset?.mediaType || 'image').toLowerCase();
  const publicId = safe(asset?.blobPath);
  const token = toCldToken(publicId);
  if (!token) return '';
  if (insertMode === 'link') return `${token}`;
  if (insertMode === 'video' || type === 'video')
    return `\n<video controls src="${token}"></video>\n`;
  const alt = safe(asset?.altText || '');
  const altEsc = alt.replace(/\]/g, '\\]');
  return `\n![${altEsc || 'Image'}](${token})\n`;
}

export default function EditorPane({
  mode,
  selectedFile,
  schemaFields,
  frontmatterObj,
  markdownBody,
  showAllOptional,
  dirty,
  loadingFile,
  saving,
  deleting,
  publishing,
  publishStatus,
  previewUrls,
  publishSuccess,
  publishConfirmSignal,
  onDismissPublishSuccess,
  error,
  onToggleShowAllOptional,
  onFrontmatterChange,
  onMarkdownChange,
  onSaveDraft,
  onPublish,
  onRestoreToLastPublish,
  onDelete,
  createCollection,
}) {
  const [createFilename, setCreateFilename] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaTarget, setMediaTarget] = useState({ kind: 'markdown' });
  const [pendingAsset, setPendingAsset] = useState(null);
  const [insertModeOpen, setInsertModeOpen] = useState(false);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const markdownRef = useRef(null);

  const missingRequired = useMemo(() => {
    if (loadingFile || !schemaFields) return [];
    return validateRequired(frontmatterObj, schemaFields);
  }, [frontmatterObj, loadingFile, schemaFields]);

  const title = useMemo(() => {
    if (mode === 'edit')
      return humanizeLabel(
        stripMarkdownExtension(selectedFile?.name || 'Editing')
      );
    if (mode === 'create')
      return createCollection
        ? `New ${humanizeLabel(createCollection)} item`
        : 'New file';
    return 'Select a file';
  }, [createCollection, mode, selectedFile?.name]);

  const isSettingsFile =
    selectedFile?.collection === 'settings' || createCollection === 'settings';
  const isBusy = saving || deleting || publishing || loadingFile;

  const canSaveDraft = useMemo(() => {
    if (isBusy || !dirty || missingRequired.length > 0) return false;
    if (mode === 'create') return Boolean(safe(createFilename).trim());
    return mode === 'edit';
  }, [createFilename, dirty, isBusy, missingRequired.length, mode]);

  const canPublish = useMemo(() => {
    if (isBusy || dirty) return false;
    if (!selectedFile?.path && mode !== 'edit') return false;
    return Number(publishStatus?.ahead_by ?? 0) > 0;
  }, [dirty, isBusy, mode, publishStatus?.ahead_by, selectedFile?.path]);

  const canRestore = useMemo(() => {
    if (isBusy) return false;
    return Number(publishStatus?.ahead_by ?? 0) > 0;
  }, [isBusy, publishStatus?.ahead_by]);

  const handleSaveDraft = async () => {
    if (mode === 'create') {
      const fn = safe(createFilename).trim();
      return onSaveDraft({
        mode: 'create',
        path: buildContentPath(createCollection, fn),
      });
    }
    return onSaveDraft({ mode: 'edit', path: selectedFile?.path });
  };

  const handleSaveAndPublish = useCallback(async () => {
    const saved =
      mode === 'create'
        ? await onSaveDraft({
            mode: 'create',
            path: buildContentPath(
              createCollection,
              safe(createFilename).trim()
            ),
          })
        : await onSaveDraft({ mode: 'edit', path: selectedFile?.path });

    // A failed save used to still pop the publish confirmation, so the
    // operator confirmed a publish that then refused with "save your draft
    // before publishing" - two errors for one problem. The save's own error
    // is already on screen; stop here.
    if (saved === false) return;

    setPublishConfirmOpen(true);
  }, [createCollection, createFilename, mode, onSaveDraft, selectedFile?.path]);

  // The Content page's publish keyboard shortcut bumps `publishConfirmSignal`
  // rather than publishing outright, so it lands on the same confirmation as
  // the button.
  //
  // Keyed on the signal alone on purpose: it must fire when the shortcut
  // fires, not whenever busy/dirty/publish state shifts underneath it. The
  // other values are read as a guard at that moment, not subscribed to.
  useEffect(() => {
    if (!publishConfirmSignal) return;
    if (isBusy || dirty) return;
    if (Number(publishStatus?.ahead_by ?? 0) <= 0) return;
    setPublishConfirmOpen(true);
  }, [publishConfirmSignal]);

  const handleConfirmPublish = useCallback(() => {
    setPublishConfirmOpen(false);
    onPublish?.();
  }, [onPublish]);

  const handlePublishFromMenu = useCallback(() => {
    setPublishConfirmOpen(true);
  }, []);

  const openMediaForMarkdown = () => {
    setMediaTarget({ kind: 'markdown' });
    setMediaOpen(true);
  };
  const openMediaForFrontmatter = ({ path }) => {
    setMediaTarget({ kind: 'frontmatter', path });
    setMediaOpen(true);
  };

  const applyFrontmatterToken = useCallback(
    (path, token) => {
      onFrontmatterChange(setAtPath(frontmatterObj, path, token));
    },
    [frontmatterObj, onFrontmatterChange]
  );

  const handleMediaPicked = useCallback(
    async (asset) => {
      if (!asset) return;
      try {
        const pid = safe(asset?.blobPath);
        if (pid) await markRecentlyUsed(pid);
      } catch (e) {
        /* best-effort tracking */
      }
      if (mediaTarget.kind === 'frontmatter') {
        const token = toCldToken(safe(asset?.blobPath));
        if (token) applyFrontmatterToken(mediaTarget.path, token);
        return;
      }
      setPendingAsset(asset);
      setInsertModeOpen(true);
    },
    [applyFrontmatterToken, mediaTarget.kind, mediaTarget.path]
  );

  const commitMarkdownInsert = useCallback(
    (insertMode) => {
      const asset = pendingAsset;
      setPendingAsset(null);
      setInsertModeOpen(false);
      if (!asset) return;
      const snippet = buildInsertSnippet(asset, insertMode);
      if (!snippet) return;
      const textarea = markdownRef.current;
      const { next, caret } = insertAtCursor(textarea, snippet);
      onMarkdownChange(next);
      setTimeout(() => {
        try {
          if (textarea) {
            textarea.focus();
            textarea.selectionStart = caret;
            textarea.selectionEnd = caret;
          }
        } catch (e) {
          /* focus may fail in some environments */
        }
      }, 0);
    },
    [onMarkdownChange, pendingAsset]
  );

  const expectedDelete =
    selectedFile?.name || selectedFile?.path?.split('/').pop() || '';

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="column" alignItems="flex-start" spacing={1.5}>
        <Box sx={{ minWidth: 0 }}>
          {mode === 'edit' && selectedFile?.collection ? (
            <Typography
              variant="caption"
              sx={{
                opacity: 0.5,
                fontWeight: 600,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
              }}
            >
              {humanizeLabel(selectedFile.collection)}
            </Typography>
          ) : null}
          <Typography variant="h6" sx={{ fontWeight: 900 }} noWrap>
            {title}
          </Typography>
          {mode !== 'none' && publishStatus?.status ? (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mt: 0.75, flexWrap: 'wrap' }}
            >
              {Number(publishStatus?.ahead_by ?? 0) > 0 ? (
                <StatusPill
                  kind="warning"
                  dot
                  label="Draft · unpublished changes"
                />
              ) : (
                <StatusPill
                  kind="success"
                  dot
                  label="Published · live on your website"
                />
              )}
              {Number(publishStatus?.ahead_by ?? 0) > 0 &&
              previewUrls?.uatUrl ? (
                <Link
                  href={previewUrls.uatUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  Preview at {previewUrls.uatUrl.replace(/^https?:\/\//, '')}
                  <LaunchIcon sx={{ fontSize: 12 }} />
                </Link>
              ) : null}
            </Stack>
          ) : null}
        </Box>

        {mode !== 'none' ? (
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: 'wrap', alignItems: 'center' }}
          >
            {canPublish && !dirty ? (
              <Button
                variant="contained"
                color="success"
                startIcon={<PublishIcon />}
                disabled={isBusy}
                onClick={handlePublishFromMenu}
                sx={{ fontWeight: 900 }}
              >
                {publishing ? 'Publishing…' : 'Publish'}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="success"
                startIcon={<PublishIcon />}
                disabled={!canSaveDraft}
                onClick={handleSaveAndPublish}
                sx={{ fontWeight: 900 }}
              >
                {saving
                  ? 'Saving…'
                  : publishing
                    ? 'Publishing…'
                    : 'Save & Publish'}
              </Button>
            )}

            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              disabled={!canSaveDraft}
              onClick={handleSaveDraft}
              sx={{ fontWeight: 900 }}
            >
              Save Draft
            </Button>

            <Button
              variant="outlined"
              startIcon={<PhotoLibraryOutlinedIcon />}
              disabled={isBusy}
              onClick={openMediaForMarkdown}
              sx={{ fontWeight: 900 }}
            >
              Add Image
            </Button>

            {mode === 'edit' && !isSettingsFile ? (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                disabled={isBusy}
                onClick={() => {
                  setDeleteConfirm('');
                  setDeleteOpen(true);
                }}
                sx={{ fontWeight: 900 }}
              >
                Delete
              </Button>
            ) : null}

            {mode === 'edit' && (
              <>
                <IconButton
                  onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                  disabled={isBusy}
                  aria-label="More actions"
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={moreMenuAnchor}
                  open={Boolean(moreMenuAnchor)}
                  onClose={() => setMoreMenuAnchor(null)}
                >
                  <MenuItem
                    onClick={() => {
                      setMoreMenuAnchor(null);
                      handlePublishFromMenu();
                    }}
                    disabled={!canPublish}
                  >
                    <ListItemIcon>
                      <PublishIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Publish without changes</ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setMoreMenuAnchor(null);
                      setRestoreOpen(true);
                    }}
                    disabled={!canRestore}
                  >
                    <ListItemIcon>
                      <RestoreIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Restore to published version</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>
        ) : null}

        {mode !== 'none' && (
          <SaveStatusIndicator
            saving={saving}
            dirty={dirty}
            publishing={publishing}
            deleting={deleting}
          />
        )}
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : null}
      {publishSuccess ? (
        <Alert
          severity="success"
          sx={{ mt: 2 }}
          onClose={onDismissPublishSuccess}
        >
          <strong>Published!</strong> Your changes are now live. It may take a
          minute or two to appear. Try opening your site in a private or
          incognito window if changes are not visible yet.
        </Alert>
      ) : null}
      {isSettingsFile ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          These are your site-wide settings. Changes here affect every page on
          your website.
        </Alert>
      ) : null}
      {missingRequired.length > 0 ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Please fill in these fields before saving:{' '}
          {missingRequired.join(', ')}
        </Alert>
      ) : null}

      <Box sx={{ mt: 2 }}>
        {mode === 'none' ? (
          <Surface sx={{ p: 5, textAlign: 'center' }}>
            <DescriptionOutlinedIcon
              sx={{ fontSize: 48, opacity: 0.25, mb: 1.5 }}
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Choose something to edit
            </Typography>
            <Typography
              variant="body2"
              sx={{ opacity: 0.6, mt: 0.5, maxWidth: 360, mx: 'auto' }}
            >
              Pick a page, blog post, or other content from the list on the
              left. You can also click <strong>+ New</strong> to create
              something.
            </Typography>
          </Surface>
        ) : (
          <Stack spacing={2}>
            {mode === 'create' ? (
              <Surface sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                  New file details
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Collection"
                    value={createCollection || ''}
                    fullWidth
                    size="small"
                    disabled
                  />
                  <TextField
                    label="Name"
                    value={createFilename}
                    onChange={(e) => setCreateFilename(e.target.value)}
                    fullWidth
                    size="small"
                    helperText="Use letters, numbers, and hyphens. Example: my-new-post"
                  />
                </Stack>
              </Surface>
            ) : null}

            {/* Lead with AI: the copilot is the primary way to build or edit
                this page (chat -> propose -> one-click approve). The manual
                field form below is the fallback, not the default path. */}
            {!isSettingsFile && schemaFields ? (
              <CopilotBar
                frontmatterObj={frontmatterObj}
                markdownBody={markdownBody}
                onApplyField={(field, value) =>
                  onFrontmatterChange(setAtPath(frontmatterObj, field, value))
                }
                onApplyBody={(value) => onMarkdownChange(value)}
              />
            ) : null}

            <Surface sx={{ p: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                  {isSettingsFile ? 'Site Settings' : 'Content'}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(showAllOptional)}
                      onChange={(e) =>
                        onToggleShowAllOptional(e.target.checked)
                      }
                    />
                  }
                  label={
                    <Typography variant="body2">Show all fields</Typography>
                  }
                />
              </Stack>

              {!schemaFields ? (
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} variant="rounded" height={40} />
                  ))}
                </Stack>
              ) : isSettingsFile ? (
                <Box sx={{ mt: 2 }}>
                  <SettingsTabs
                    schemaFields={schemaFields}
                    frontmatterObj={frontmatterObj}
                    showAllOptional={showAllOptional}
                    onChange={onFrontmatterChange}
                    onPickMedia={openMediaForFrontmatter}
                  />
                </Box>
              ) : (
                <Stack spacing={1.25} sx={{ mt: 2 }}>
                  {Object.entries(schemaFields).map(([key, spec]) => (
                    <FrontmatterField
                      key={key}
                      path={key}
                      spec={spec}
                      frontmatterObj={frontmatterObj}
                      showAllOptional={showAllOptional}
                      onChange={onFrontmatterChange}
                      onPickMedia={openMediaForFrontmatter}
                    />
                  ))}
                </Stack>
              )}
            </Surface>

            {!isSettingsFile && schemaFields ? (
              <SeoAssistCard
                schemaFields={schemaFields}
                frontmatterObj={frontmatterObj}
                markdownBody={markdownBody}
                onApply={(field, value) =>
                  onFrontmatterChange(setAtPath(frontmatterObj, field, value))
                }
              />
            ) : null}

            {selectedFile?.collection === 'blog' ||
            createCollection === 'blog' ? (
              <BlogContextCard frontmatter={frontmatterObj} />
            ) : null}

            <Surface sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.5 }}>
                Page Content
              </Typography>
              <Typography
                variant="caption"
                sx={{ display: 'block', mb: 1.5, opacity: 0.6 }}
              >
                Write or paste your page content here. Use the Add Image button
                above to insert photos.
              </Typography>
              <TextField
                value={markdownBody}
                onChange={(e) => onMarkdownChange(e.target.value)}
                multiline
                minRows={14}
                fullWidth
                placeholder="Start writing…"
                inputRef={markdownRef}
                inputProps={{
                  style: {
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: 13,
                  },
                }}
              />
            </Surface>
          </Stack>
        )}
      </Box>

      {/* Publish confirmation */}
      <PublishConfirmDialog
        open={publishConfirmOpen}
        onClose={() => setPublishConfirmOpen(false)}
        onConfirm={handleConfirmPublish}
        publishing={publishing}
      />

      {/* Restore confirmation */}
      <Dialog open={restoreOpen} onClose={() => setRestoreOpen(false)}>
        <DialogTitle>Restore to published version?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will undo all your draft changes and restore the content to
            match the version currently live on your website.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreOpen(false)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => {
              setRestoreOpen(false);
              onRestoreToLastPublish();
            }}
          >
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      <MediaPickerModal
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        onPick={async (asset) => {
          await handleMediaPicked(asset);
          setMediaOpen(false);
        }}
      />
      <InsertModeDialog
        open={insertModeOpen}
        asset={pendingAsset}
        onClose={() => {
          setInsertModeOpen(false);
          setPendingAsset(null);
        }}
        onChoose={(m) => commitMarkdownInsert(m)}
      />

      {/* Delete confirmation */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete this content?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            This will permanently remove this file from your website. This
            cannot be undone.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Type <strong>{expectedDelete}</strong> to confirm.
          </Typography>
          <TextField
            sx={{ mt: 2 }}
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            fullWidth
            placeholder="Type filename to confirm"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleting || deleteConfirm.trim() !== expectedDelete}
            onClick={async () => {
              await onDelete();
              setDeleteOpen(false);
            }}
            sx={{ fontWeight: 900 }}
          >
            Delete permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
